// frontend-react/src/components/IAEditor.js - ACTUALIZADO

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import { adminService, blogService } from "../api/services";

const IAEditor = ({ categories, onPostCreated }) => {
  const safeCategories = Array.isArray(categories) ? categories : [];

  const [formData, setFormData] = useState({
    category_id: "",
    topic: "",
  });
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [includeImages, setIncludeImages] = useState(true);
  const [useSearch, setUseSearch] = useState(false); // ✅ Estado para búsqueda
  const [imageSource, setImageSource] = useState('generate'); // ✅ 'generate' | 'search'
  const [imageCount, setImageCount] = useState(3);
  const [generatedImages, setGeneratedImages] = useState([]);

  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [postId, setPostId] = useState(null);

  // Polling para verificar imágenes
  React.useEffect(() => {
    let intervalId;

    if (isGeneratingImages && postId) {
      intervalId = setInterval(async () => {
        try {
          // Agregar timestamp para evitar caché
          const response = await adminService.getPost(postId, { params: { t: Date.now() } });
          const updatedPost = response.data;
          
          console.log("🔄 Polling post:", postId, "Content length:", updatedPost.content.length);
          
          // Verificar si el contenido ha cambiado (tiene imágenes insertadas)
          // Buscamos <img o <figure, o si hay imágenes en el array images
          const hasImagesInContent = updatedPost.content.toLowerCase().includes('<img') || 
                                     updatedPost.content.toLowerCase().includes('<figure');
          const hasImagesInArray = updatedPost.images && updatedPost.images.length > 0;

          if (hasImagesInContent || (hasImagesInArray && updatedPost.content.length > generatedContent.content.length)) {
             console.log("✅ Imágenes detectadas! Actualizando editor.");
             setGeneratedContent(prev => ({
                 ...prev,
                 content: updatedPost.content,
                 image_suggestions: updatedPost.images.map(img => `Imagen generada: ${img.description}`) // Actualizar sugerencias también
             }));
             setGeneratedImages(updatedPost.images.map(img => `Imagen generada: ${img.description}`));
             setIsGeneratingImages(false);
             setSuccess("¡Imágenes generadas e insertadas exitosamente!");
          }
        } catch (err) {
          console.error("Error polling post:", err);
        }
      }, 5000); // Cada 5 segundos
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isGeneratingImages, postId]);

  const handleGenerate = async () => {
    if (!formData.category_id) {
      setError("Selecciona una categoría");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setIsGeneratingImages(false);
    
    try {
      const response = await adminService.generateBlog({
        ...formData,
        ...formData,
        include_images: includeImages,
        use_search: useSearch, // ✅ Enviar parámetro
        image_source: imageSource, // ✅ Enviar fuente de imágenes
        image_count: imageCount,
      });

      console.log("📊 Response from backend:", response.data);
      
      // ✅ FIXED: Backend ahora envía 'post' en lugar de 'generated_content'
      if (response.data.post) {
        const post = response.data.post;
        
        // Convertir post a formato esperado por el editor
        const generatedData = {
          title: post.title,
          content: post.content,
          excerpt: post.excerpt,
          image_suggestions: post.images?.map(img => img.description) || []
        };
        
        console.log("✅ Contenido recibido:", {
          title: generatedData.title,
          contentLength: generatedData.content.length,
          excerpt: generatedData.excerpt?.substring(0, 50),
          imageSuggestions: generatedData.image_suggestions.length
        });
        
        setGeneratedContent(generatedData);
        setGeneratedImages(generatedData.image_suggestions || []);
        setPostId(post.id);
        
        if (response.data.images_processing) {
          setIsGeneratingImages(true);
          setSuccess("Texto generado. Generando imágenes en segundo plano...");
        } else {
          setSuccess("Contenido generado exitosamente");
        }
      } else {
        console.error("❌ No se encontró 'post' en response.data");
        setError("No se recibió contenido generado");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "Error generando contenido. Inténtalo de nuevo.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (status = "draft") => {
    if (!generatedContent) return;

    try {
      const categorySlug = safeCategories.find(
        (cat) => cat.id === formData.category_id
      )?.slug;

      const postData = {
        title: generatedContent.title,
        content: generatedContent.content,
        excerpt:
          generatedContent.excerpt ||
          generatedContent.content.substring(0, 150) + "...",
        category_slugs: categorySlug ? [categorySlug] : [],
        is_auto_generated: true,
        status: status,
      };
      
      // Si ya tenemos un ID (porque se creó al generar), deberíamos actualizar en lugar de crear
      if (postId) {
          await adminService.updatePost(postId, postData);
      } else {
          await blogService.createPost(postData);
      }

      setSuccess(
        `Post ${
          status === "published" ? "publicado" : "guardado como borrador"
        } exitosamente`
      );

      // Limpiar el estado después de guardar/publicar
      setGeneratedContent(null);
      setFormData({ category_id: "", topic: "" });
      setGeneratedImages([]);
      setPostId(null);
      setIsGeneratingImages(false);

      if (onPostCreated) {
        onPostCreated();
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "Error guardando el post. Verifica la conexión o los permisos.";
      setError(errorMessage);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generar Blog con IA
            </Typography>

            <TextField
              select
              fullWidth
              label="Categoría"
              value={formData.category_id}
              onChange={(e) =>
                setFormData({ ...formData, category_id: e.target.value })
              }
              sx={{ mb: 2 }}
              required
            >
              <MenuItem value="">
                <em>Selecciona una categoría</em>
              </MenuItem>
              {safeCategories.length > 0 ? (
                safeCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No hay categorías disponibles</MenuItem>
              )}
            </TextField>

            <TextField
              fullWidth
              label="Tema específico (opcional)"
              value={formData.topic}
              onChange={(e) =>
                setFormData({ ...formData, topic: e.target.value })
              }
              placeholder="Ej: últimas tendencias en inteligencia artificial"
              sx={{ mb: 2 }}
            />

            {/* Controles de imágenes */}
            <FormControlLabel
              control={
                <Switch
                  checked={includeImages}
                  onChange={(e) => setIncludeImages(e.target.checked)}
                  color="primary"
                />
              }
              label="Incluir imágenes generadas por IA"
              sx={{ mb: 2 }}
            />

            {/* Control de Búsqueda Web */}
            <FormControlLabel
              control={
                <Switch
                  checked={useSearch}
                  onChange={(e) => setUseSearch(e.target.checked)}
                  color="secondary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Buscar en Internet (Datos actualizados)</Typography>
                  <Chip label="Nuevo" size="small" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />
                </Box>
              }
              sx={{ mb: 1 }}
            />

            {includeImages && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Fuente de Imágenes:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button 
                    variant={imageSource === 'generate' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setImageSource('generate')}
                    color="primary"
                  >
                    🤖 Generar (IA)
                  </Button>
                  <Button 
                    variant={imageSource === 'search' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setImageSource('search')}
                    color="secondary"
                  >
                    🔍 Buscar (Internet)
                  </Button>
                </Box>
              </Box>
            )}

            {includeImages && (
              <TextField
                select
                fullWidth
                label="Cantidad de imágenes"
                value={imageCount}
                onChange={(e) => setImageCount(parseInt(e.target.value))}
                size="small"
                sx={{ mb: 2 }}
              >
                <MenuItem value={1}>1 imagen</MenuItem>
                <MenuItem value={2}>2 imágenes</MenuItem>
                <MenuItem value={3}>3 imágenes</MenuItem>
              </TextField>
            )}

            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading || !formData.category_id}
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Generando..." : "Generar Contenido"}
            </Button>

            {safeCategories.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No hay categorías disponibles. Crea categorías primero.
              </Alert>
            )}

            {/* Visualización de imágenes generadas */}
            {generatedImages.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Imágenes generadas:
                </Typography>
                {generatedImages.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        {generatedContent && (
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Vista Previa</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                        if (postId) {
                            try {
                                const response = await adminService.getPost(postId, { params: { t: Date.now() } });
                                setGeneratedContent(prev => ({
                                    ...prev,
                                    content: response.data.content
                                }));
                                setSuccess("Contenido actualizado manualmente");
                            } catch(e) {
                                setError("Error actualizando: " + e.message);
                            }
                        }
                    }}
                  >
                    🔄 Actualizar
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleSave("draft")}
                  >
                    Guardar Borrador
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleSave("published")}
                  >
                    Publicar
                  </Button>
                </Box>
              </Box>
              
              {isGeneratingImages && (
                  <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
                      Generando imágenes en segundo plano... El contenido se actualizará automáticamente.
                  </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Título del Post"
                  value={generatedContent.title}
                  onChange={(e) =>
                    setGeneratedContent({
                      ...generatedContent,
                      title: e.target.value,
                    })
                  }
                  sx={{ mb: 1 }}
                  InputProps={{
                    style: { fontSize: "1.5rem", fontWeight: "bold" },
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  label="Resumen / Extracto"
                  value={generatedContent.excerpt}
                  onChange={(e) =>
                    setGeneratedContent({
                      ...generatedContent,
                      excerpt: e.target.value,
                    })
                  }
                  rows={2}
                  sx={{ mb: 2 }}
                />

                <Typography
                  variant="subtitle1"
                  sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
                >
                  Contenido Principal:
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  value={generatedContent.content}
                  onChange={(e) =>
                    setGeneratedContent({
                      ...generatedContent,
                      content: e.target.value,
                    })
                  }
                  rows={15}
                  sx={{ mb: 2 }}
                />
                
                {/* Vista previa HTML para ver las imágenes */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Vista Previa HTML (con imágenes):
                </Typography>
                <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, maxHeight: '400px', overflow: 'auto' }}>
                    <div dangerouslySetInnerHTML={{ __html: generatedContent.content }} />
                </Box>
                
              </Paper>

              {generatedContent.image_suggestions &&
                Array.isArray(generatedContent.image_suggestions) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Sugerencias de imágenes:
                    </Typography>
                    <ul>
                      {generatedContent.image_suggestions.map(
                        (suggestion, index) => (
                          <li key={index}>
                            <Typography variant="body2">
                              {suggestion}
                            </Typography>
                          </li>
                        )
                      )}
                    </ul>
                  </Box>
                )}
            </CardContent>
          </Card>
        )}
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Grid>
      )}

      {success && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default IAEditor;
